/**
 * Keep a floating panel inside the viewport.
 *
 * Lived in sections/globe/common/popup-position.ts, which
 * common/performance/performance-panel.tsx reached into — a shared component
 * importing from one section. The rest of that file was
 * `calculateNonOverlappingPosition` and a `POPUP_SELECTORS` list of hard-coded
 * class names (`.city-panel`, `.segment-popup`, …) that walked the DOM looking
 * for collisions. Nothing called it, and the class-name lookup would have
 * broken silently the moment those sheets became CSS Modules, so it went rather
 * than moving with this.
 *
 * `viewport` is a parameter rather than a read of `window` so the clamping can
 * be reasoned about — and tested — without a DOM; it defaults to the window.
 * It moved here from components/game/common when the anchored layers in
 * components/ui needed it: it knows nothing of the game. `margin` keeps a layer
 * that far from the edge.
 *
 * `placeBeside` is the one placement every floating layer uses — Popover and
 * HoverCard beside their anchor, Tooltip beside the cursor or centred beside
 * an icon button: open on the side asked for, flip to the other side when only
 * that one has room, then clamp.
 */

import type { Box, Size } from './box';

interface Position {
  x: number;
  y: number;
}

/** Which side of its anchor a layer opens on. */
export type Side = 'below' | 'above' | 'left' | 'right';

/** Where along the anchor it sits: from the anchor's start, or centred on it. */
export type Align = 'start' | 'center';

/** The window, read when a placement is asked for rather than when the module loads. */
const windowViewport = () => ({ width: window.innerWidth, height: window.innerHeight });

export function clampToViewport(
  x: number,
  y: number,
  dimensions: Size,
  viewport: Size = windowViewport(),
  margin = 0,
): Position {
  return {
    // `Math.max(margin, …)` last: a panel wider than the viewport pins to the
    // left edge rather than off-screen to the left, which is what the min
    // alone would give.
    x: Math.max(margin, Math.min(viewport.width - dimensions.width - margin, x)),
    y: Math.max(margin, Math.min(viewport.height - dimensions.height - margin, y)),
  };
}

/**
 * One axis: the first candidate at which the layer fits between the margins,
 * or — when none does — the first, clamped. Candidates are the layer's near
 * edge, in the order to try them.
 */
function settle(candidates: number[], size: number, extent: number, margin: number): number {
  const fits = candidates.find((at) => at >= margin && at + size <= extent - margin);
  // `Math.max(margin, …)` last, as in clampToViewport: a layer bigger than the
  // viewport pins to the start edge rather than off it.
  return Math.max(margin, Math.min(extent - size - margin, fits ?? candidates[0]));
}

/**
 * Where a layer of `size` goes beside `anchor`: on `side`, `gap` away, its
 * start aligned with the anchor's — flipped to the opposite side when it only
 * fits there, and to the anchor's other end when it only fits that way — then
 * clamped `margin` inside the viewport.
 *
 * `crossOffset` moves it along the anchor, away from the start: a tooltip
 * beside the cursor sits diagonally off it, `gap` to the right and
 * `crossOffset` down, so the pointer never covers it. `align: 'center'` centres
 * it along the anchor instead — an icon button's name beside the button.
 */
export function placeBeside(
  anchor: Box,
  size: Size,
  side: Side,
  {
    gap = 8,
    margin = 8,
    crossOffset = 0,
    align = 'start',
    viewport = windowViewport(),
  }: {
    gap?: number;
    margin?: number;
    crossOffset?: number;
    align?: Align;
    viewport?: Size;
  } = {},
): Position {
  const after = { x: anchor.right + gap, y: anchor.bottom + gap };
  const before = { x: anchor.left - gap - size.width, y: anchor.top - gap - size.height };
  // Centred: the layer's middle on the anchor's, tried before the ends.
  const centre = {
    x: (anchor.left + anchor.right - size.width) / 2,
    y: (anchor.top + anchor.bottom - size.height) / 2,
  };
  const start = align === 'center' ? centre : { x: anchor.left + crossOffset, y: anchor.top + crossOffset };
  const end = { x: anchor.right - size.width - crossOffset, y: anchor.bottom - size.height - crossOffset };
  const [xs, ys] = {
    below: [[start.x, end.x], [after.y, before.y]],
    above: [[start.x, end.x], [before.y, after.y]],
    right: [[after.x, before.x], [start.y, end.y]],
    left: [[before.x, after.x], [start.y, end.y]],
  }[side];
  return {
    x: settle(xs, size.width, viewport.width, margin),
    y: settle(ys, size.height, viewport.height, margin),
  };
}
